import { z } from "zod";
import { PHONE_FORMAT } from "@shared/formats/phone";
import { EIN_FORMAT } from "@shared/formats/ein";
import { POSTAL_CODE_FORMAT } from "@shared/formats/postal-code";
import { URL_FORMAT } from "@shared/formats/url";
import { EMAIL_FORMAT } from "@shared/formats/email";
import { STATE_FORMAT } from "@shared/formats/state";
import { ADDRESS_CONSTRAINTS } from "@shared/formats/address";
import { isProductionEnvironment } from "@/lib/public-config";

export const isValidEmail = (v: string) => EMAIL_FORMAT.validate(v);

export function emailProblemMessage(value: string): string | undefined {
  const problem = EMAIL_FORMAT.diagnose(value, { rejectReserved: isProductionEnvironment() });
  return problem ? EMAIL_FORMAT.problemMessage(problem) : undefined;
}

export const isCapturableEmail = (value: string) =>
  isValidEmail(value) && !emailProblemMessage(value);

function refineEmailDomain(value: string, ctx: z.RefinementCtx) {
  const message = value ? emailProblemMessage(value) : undefined;
  if (message) ctx.addIssue({ code: "custom", message });
}
export const isValidPhone = (v: string) => PHONE_FORMAT.validate(v);
export const isValidZip = (v: string) => POSTAL_CODE_FORMAT.validate(v);
export const isValidEin = (v: string) => EIN_FORMAT.validate(v);
export const isValidUrl = (v: string) => URL_FORMAT.validate(v);
export const isValidState = (v: string) => STATE_FORMAT.validate(v);

export const normalizeZipInput = (value: string) => {
  const upper = value.toUpperCase();
  if (/[A-Z]/.test(upper)) {
    return upper.replace(/[^A-Z0-9 ]/g, "").slice(0, 7);
  }
  const digits = upper.replace(/\D/g, "").slice(0, 9);
  return digits.length <= 5
    ? digits
    : `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

export const validators = {
  text: {
    required: (msg = "Required") => z.string().min(1, msg),
    optional: () => z.string().optional().or(z.literal("")),
  },

  email: {
    required: (msg = "Email is required") =>
      z
        .string()
        .min(1, msg)
        .refine(isValidEmail, EMAIL_FORMAT.errorMessage)
        .superRefine(refineEmailDomain),
    optional: () =>
      z
        .string()
        .refine((v) => !v || isValidEmail(v), EMAIL_FORMAT.errorMessage)
        .superRefine(refineEmailDomain)
        .optional()
        .or(z.literal("")),
  },

  phone: {
    required: (msg = "Phone is required") =>
      z
        .string()
        .min(1, msg)
        .superRefine((value, ctx) => {
          if (!isValidPhone(value)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                PHONE_FORMAT.diagnose(value) ?? PHONE_FORMAT.errorMessage,
            });
          }
        }),
    optional: () =>
      z
        .string()
        .superRefine((value, ctx) => {
          if (value && !isValidPhone(value)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                PHONE_FORMAT.diagnose(value) ?? PHONE_FORMAT.errorMessage,
            });
          }
        })
        .optional()
        .or(z.literal("")),
  },

  postalCode: {
    required: (msg = "ZIP code is required") =>
      z
        .string()
        .min(1, msg)
        .refine(isValidZip, POSTAL_CODE_FORMAT.errorMessage),
    optional: () =>
      z
        .string()
        .refine((v) => !v || isValidZip(v), POSTAL_CODE_FORMAT.errorMessage)
        .optional()
        .or(z.literal("")),
  },

  state: {
    required: (msg = "State is required") =>
      z.string().min(1, msg).refine(isValidState, STATE_FORMAT.errorMessage),
    optional: () =>
      z
        .string()
        .refine((v) => !v || isValidState(v), STATE_FORMAT.errorMessage)
        .optional()
        .or(z.literal("")),
  },

  country: {
    required: (msg = "Country is required") =>
      z
        .string()
        .min(1, msg)
        .transform((v) => v.trim().toUpperCase())
        .pipe(
          z
            .string()
            .regex(
              ADDRESS_CONSTRAINTS.country.pattern,
              ADDRESS_CONSTRAINTS.country.errorMessage,
            ),
        ),
  },

  city: {
    required: (msg = "City is required") =>
      z
        .string()
        .min(1, msg)
        .max(
          ADDRESS_CONSTRAINTS.city.maxLength,
          ADDRESS_CONSTRAINTS.city.errorMessage,
        ),
    optional: () =>
      z
        .string()
        .max(
          ADDRESS_CONSTRAINTS.city.maxLength,
          ADDRESS_CONSTRAINTS.city.errorMessage,
        )
        .optional()
        .or(z.literal("")),
  },

  line1: {
    required: (msg = "Street address is required") =>
      z
        .string()
        .min(1, msg)
        .max(
          ADDRESS_CONSTRAINTS.line1.maxLength,
          ADDRESS_CONSTRAINTS.line1.errorMessage,
        ),
    optional: () =>
      z
        .string()
        .max(
          ADDRESS_CONSTRAINTS.line1.maxLength,
          ADDRESS_CONSTRAINTS.line1.errorMessage,
        )
        .optional()
        .or(z.literal("")),
  },

  line2: {
    optional: () =>
      z
        .string()
        .max(
          ADDRESS_CONSTRAINTS.line2.maxLength,
          ADDRESS_CONSTRAINTS.line2.errorMessage,
        )
        .optional()
        .or(z.literal("")),
  },

  url: {
    required: (msg = "URL is required") =>
      z.string().min(1, msg).refine(isValidUrl, URL_FORMAT.errorMessage),
    optional: () =>
      z
        .string()
        .refine((v) => !v || isValidUrl(v), URL_FORMAT.errorMessage)
        .optional()
        .or(z.literal("")),
  },

  ein: {
    optional: () =>
      z
        .string()
        .refine((v) => !v || isValidEin(v), EIN_FORMAT.errorMessage)
        .optional()
        .or(z.literal("")),
  },

  message: {
    required: (msg = "Message is required") =>
      z.string().min(1, msg).max(5000, "Max 5000 characters"),
    optional: () =>
      z.string().max(5000, "Max 5000 characters").optional().or(z.literal("")),
  },

  consent: z.boolean().refine((v) => v === true, "You must agree to continue"),

  number: {
    required: () => z.number(),
    optional: () => z.number().optional(),
    positive: (msg = "Must be positive") => z.number().positive(msg),
    int: (msg = "Must be a whole number") =>
      z.number().int(msg).positive("Must be positive"),
    nonNegative: (msg = "Must be non-negative") => z.number().nonnegative(msg),
  },
};
